import { push } from 'connected-react-router';
import { API_URL } from 'data/services/apiConstants';
import { get } from 'data/services/apiService';
import { propertyRepoThunks } from 'data/repositories/propertyRepository';
import {
	addressRequestSuccess,
	addressRequestError,
} from 'reducers/addressReducer';

export const GET_PROPERTY_REQUEST = 'GET_PROPERTY_REQUEST';
export const GET_PROPERTY_SUCCESS = 'GET_PROPERTY_SUCCESS';
export const GET_PROPERTY_ERROR = 'GET_PROPERTY_ERROR';
export const UPDATE_PROPERTY_COUNT = 'UPDATE_PROPERTY_COUNT';
export const UPDATE_PROPERTY_COUNTS = 'UPDATE_PROPERTY_COUNTS';
export const UPDATE_PROPERTY_FDNY = 'UPDATE_PROPERTY_FDNY';
export const UPDATE_PROPERTY_TOTALS = 'UPDATE_PROPERTY_TOTALS';
export const UPDATE_PROPERTY_LUNA = 'UPDATE_PROPERTY_LUNA';

const initialState = {
	propertyData: null,
	isLoading: false,
	error: null,
};

// Removed postNewProperty - backend now handles property creation internally
// Frontend just calls fetchProperty which triggers backend GOAT/Lambda fallback

export const fetchProperty =
	(addressWithoutUSA, onSelect = null) =>
	(dispatch, getState) => {
		let url = `${API_URL}/api/property/overview/${encodeURIComponent(
			addressWithoutUSA,
		)}`;
		if (!onSelect) {
			dispatch(propertyRequest());
		}

		return get({
			dispatch,
			url,
			onSuccess: (result) =>
				onPropertySuccess(result?.property, dispatch, getState, onSelect),
			onError: (error) => onPropertyError(error, dispatch, getState, onSelect),
		});
	};

/**
 * Fetch the overview's lazy sub-resources (FDNY, totals, luna images) via the
 * property repository and bridge each result onto `propertyData` so existing
 * consumers keep working while the core overview payload stays lean. Per-dataset
 * open counts are NOT fetched here — they stay embedded in the overview response
 * so their badges render on first paint.
 */
export const hydratePropertyLazyData = (dispatch, propertyId) => {
	if (!propertyId) {
		return;
	}

	dispatch(refreshPropertyCounts(propertyId));

	dispatch(propertyRepoThunks.getTotals({ propertyId }))
		.then((result) => {
			if (result) {
				dispatch(updatePropertyTotals(result.totals));
			}
		})
		.catch(() => {});

	dispatch(propertyRepoThunks.getLunaImages({ propertyId }))
		.then((result) => {
			if (result) {
				dispatch(updatePropertyLuna(result.lunaImages));
			}
		})
		.catch(() => {});
};

/**
 * Re-read every badge count (the lazy /counts endpoint and the FDNY overview) and merge it
 * onto propertyData. Shared by first paint and the fallback poll; never reloads the property.
 */
export const refreshPropertyCounts = (propertyId) => (dispatch) =>
	Promise.all([
		dispatch(propertyRepoThunks.getFdnyOverview({ propertyId }))
			.then((result) => {
				if (result) {
					dispatch(
						updatePropertyFdny(
							result.fdny,
							result.fdny_nov_count,
							result.fdny_vio_count,
							result.fdny_permit_accounts_count,
						),
					);
				}
			})
			.catch(() => {}),
		dispatch(propertyRepoThunks.getCounts({ propertyId }))
			.then((result) => {
				if (result) {
					dispatch(updatePropertyCounts(result));
				}
			})
			.catch(() => {}),
	]);

export const silentlyRefetchProperty = () => (dispatch, getState) => {
	let state = getState();
	let address = state.property.propertyData?.address;
	if (address) {
		let url = `${API_URL}/api/property/overview/${encodeURIComponent(address)}`;
		return get({
			dispatch,
			url,
			onSuccess: (result) => {
				if (result?.property) {
					// Badge counts come from the lazy endpoints, not the overview row (which can
					// still hold stale denormalized *_count columns), so keep the loaded ones until
					// hydration replaces them; otherwise every Pusher event blinks the badges.
					let current = getState().property.propertyData;
					let next = result.property;
					if (current?.id === next.id) {
						let loadedCounts = Object.fromEntries(
							Object.entries(current).filter(([key]) => key.endsWith('_count')),
						);
						next = { ...current, ...next, ...loadedCounts };
					}
					dispatch(propertyRequestSuccess(next));
					hydratePropertyLazyData(dispatch, result?.property?.id);
				}
			},
		});
	}
};

function onPropertySuccess(property, dispatch, getState, onSelect = null) {
	let state = getState();

	// Handle null, missing, or invalid property - stop loading and show error
	if (
		!property ||
		!property.address ||
		typeof property.address !== 'string' ||
		property.address.trim() === ''
	) {
		dispatch(propertyRequestError({ message: 'address not found' }));
		dispatch(addressRequestError({ message: 'address not found' })); // Clear address loading state

		// Only redirect if NOT from modal selection
		if (!onSelect) {
			dispatch(push(`/property-not-found`));
		} else {
			onSelect(null); // Let modal handle error
		}
		return;
	}

	// Property found - dispatch success and update route
	dispatch(propertyRequestSuccess(property));
	hydratePropertyLazyData(dispatch, property?.id);
	dispatch(addressRequestSuccess()); // Clear address loading state
	let path = state.router.location.pathname;

	if (onSelect) {
		onSelect(property);
		return;
	}

	let pathParts = path.split('/');

	//address needs to be decoded then re-encoded to get the encoding to match, router doesn't seem to encode whitespace
	let oldPath = `/${pathParts[1]}/${encodeURIComponent(
		decodeURIComponent(pathParts[2]),
	)}/${pathParts[3]}`;
	let newPath = `/property/${encodeURIComponent(property.address)}/overview`;
	if (pathParts[1] === 'property' && pathParts[3]) {
		newPath = `/property/${encodeURIComponent(property.address)}/${
			pathParts[3]
		}`;
	}

	//check if the path changed to prevent repeats being pushed to the browser history, making the back button annoying
	if (newPath !== oldPath) {
		dispatch(push(newPath));
	}
}

function onPropertyError(error, dispatch, getState, onSelect = null) {
	dispatch(propertyRequestError(error));
	dispatch(addressRequestError(error)); // Clear address loading state

	// Only redirect if NOT from modal
	if (!onSelect) {
		dispatch(push(`/property-not-found`));
	} else {
		onSelect(null); // Let modal handle error
	}
}

export const propertyRequest = () => ({
	type: GET_PROPERTY_REQUEST,
	payload: {
		isLoading: true,
	},
});

export const propertyRequestSuccess = (propertyData) => ({
	type: GET_PROPERTY_SUCCESS,
	payload: {
		isLoading: false,
		propertyData,
		error: null,
	},
});

export const propertyRequestError = (error) => ({
	type: GET_PROPERTY_ERROR,
	payload: {
		isLoading: false,
		propertyData: null,
		error,
	},
});

export const updatePropertyCount = (countField, countValue) => ({
	type: UPDATE_PROPERTY_COUNT,
	payload: {
		countField,
		countValue,
	},
});

/** Merges count fields pushed over websockets (periodic badge refresh). */
export const updatePropertyCounts = (counts, fdnyNovCount, fdnyVioCount) => ({
	type: UPDATE_PROPERTY_COUNTS,
	payload: {
		counts: counts || {},
		fdny_nov_count: fdnyNovCount,
		fdny_vio_count: fdnyVioCount,
	},
});

export function updatePropertyFdny(
	fdny,
	fdnyNovCount,
	fdnyVioCount,
	fdnyPermitAccountsCount,
) {
	return {
		type: UPDATE_PROPERTY_FDNY,
		payload: {
			fdny,
			fdny_nov_count: fdnyNovCount,
			fdny_vio_count: fdnyVioCount,
			fdny_permit_accounts_count: fdnyPermitAccountsCount,
		},
	};
}

export const updatePropertyTotals = (totals) => ({
	type: UPDATE_PROPERTY_TOTALS,
	payload: {
		totals,
	},
});

export const updatePropertyLuna = (lunaImages) => ({
	type: UPDATE_PROPERTY_LUNA,
	payload: {
		lunaImages,
	},
});

// Exposed for unit tests that need to exercise onPropertySuccess directly.
export const onPropertySuccessForTest = onPropertySuccess;

const propertyReducer = (state = initialState, action) => {
	switch (action.type) {
		case GET_PROPERTY_REQUEST:
		case GET_PROPERTY_SUCCESS:
		case GET_PROPERTY_ERROR:
			return {
				...state,
				...action.payload,
			};
		case UPDATE_PROPERTY_COUNT:
			if (!state.propertyData) {
				return state;
			}
			return {
				...state,
				propertyData: {
					...state.propertyData,
					[action.payload.countField]: action.payload.countValue,
				},
			};
		case UPDATE_PROPERTY_COUNTS: {
			if (!state.propertyData) {
				return state;
			}
			const nextPropertyData = {
				...state.propertyData,
				...action.payload.counts,
			};
			if (action.payload.fdny_nov_count !== undefined) {
				nextPropertyData.fdny_nov_count = action.payload.fdny_nov_count;
			}
			if (action.payload.fdny_vio_count !== undefined) {
				nextPropertyData.fdny_vio_count = action.payload.fdny_vio_count;
			}
			return {
				...state,
				propertyData: nextPropertyData,
			};
		}
		case UPDATE_PROPERTY_FDNY:
			if (!state.propertyData) {
				return state;
			}
			return {
				...state,
				propertyData: {
					...state.propertyData,
					fdny: action.payload.fdny,
					fdny_nov_count: action.payload.fdny_nov_count,
					fdny_vio_count: action.payload.fdny_vio_count,
					fdny_permit_accounts_count: action.payload.fdny_permit_accounts_count,
				},
			};
		case UPDATE_PROPERTY_TOTALS:
			if (!state.propertyData) {
				return state;
			}
			return {
				...state,
				propertyData: {
					...state.propertyData,
					totals: action.payload.totals,
				},
			};
		case UPDATE_PROPERTY_LUNA:
			if (!state.propertyData) {
				return state;
			}
			return {
				...state,
				propertyData: {
					...state.propertyData,
					lunaImages: action.payload.lunaImages,
				},
			};
		default:
			return state;
	}
};

export default propertyReducer;
